import {
  Box,
  Center,
  Collapsible,
  Flex,
  IconButton,
  Stack,
  useDisclosure,
} from '@chakra-ui/react'

import Link from '@components/Link'
import useNavigationItemsData from './hooks/use-navigation-items-data.hook'
import NavigationSubItem from './NavigationSubItem'
import NavigationItemIcon from './NavigationItemIcon'
import {ChevronDownSolidIcon} from '../icons'

export default function NavigationItem({
  icon,
  title,
  url,
  items = [],
  count,
  onClick,
}: ReturnType<typeof useNavigationItemsData>[0] & {
  onClick?: () => void
}) {
  const isActive =
    !items.length && window.location.pathname.startsWith(new URL(url).pathname)

  const {open: isOpen, onToggle} = useDisclosure({
    defaultOpen: !!items.length,
  })

  return (
    <>
      <Flex
        alignItems="center"
        borderRadius="lg"
        className={isActive ? 'navigation-item-active' : undefined}
        height="12"
        paddingInline="4"
        data-group=""
        css={{
          '&.navigation-item-active': {
            backgroundColor: !items.length
              ? {
                  base: 'blue.50',
                  xl: 'white',
                }
              : undefined,
            boxShadow: {
              base: 'none',
              xl: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
            },
          },
        }}
        _hover={
          items.length
            ? {
                textDecoration: 'none',
              }
            : {
                backgroundColor: 'white',
                textDecoration: 'none',
              }
        }
        asChild
      >
        <Link href={url} onClick={onClick}>
          <NavigationItemIcon icon={icon} />
          <Box
            color={isActive ? 'gray.800' : 'gray.500'}
            fontSize="sm"
            fontWeight="semibold"
            letterSpacing="tight"
          >
            {title}
          </Box>
          {!!count && (
            <Center
              marginInlineStart="auto"
              backgroundColor="red.500"
              color="white"
              paddingInline="1.5"
              rounded="full"
              fontSize="xs"
              fontWeight="semibold"
            >
              {`+${count}`}
            </Center>
          )}

          {items.length > 0 && (
            <IconButton
              aria-label={title}
              size="xs"
              marginInlineStart="auto"
              variant="ghost"
              onClick={(e) => {
                e.preventDefault()
                onToggle()
              }}
            >
              <ChevronDownSolidIcon
                css={{
                  transform: isOpen ? 'rotate(180deg)' : undefined,
                }}
              />
            </IconButton>
          )}
        </Link>
      </Flex>
      {items.length > 0 && (
        <Collapsible.Root open={isOpen}>
          <Collapsible.Content>
            <Stack paddingInlineStart="10" gap="2" paddingBlock="1">
              {items.map((item) => (
                <NavigationSubItem
                  {...item}
                  onClick={onClick}
                  key={item.url}
                  isActive={
                    window.location.pathname === new URL(item.url).pathname
                  }
                />
              ))}
            </Stack>
          </Collapsible.Content>
        </Collapsible.Root>
      )}
    </>
  )
}
